import { RulebookVisibility } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import appLogger from "../../../infra/logger/appLogger";
import countRulebookChanges from "./countRulebookChanges";
import { draftStorageForOwner } from "./rulebookDraftNormalization";
import { deleteRulebookDraft, getRulebookDraft, getRulebookDraftOwner, linkRulebookDraft } from "./rulebookDraftStore";
import { apiRulebookPublishingClient, EMPTY_PUBLISHED_RECORD, RulebookPublishConflictError, RulebookPublishMissingError, RulebookPublishRejectedError, } from "./rulebookPublishingClient";
import toPublishedRulebookSnapshot from "./toPublishedRulebookSnapshot";
const logger = appLogger.withTag("RulebookEditor");
/**
 * The creator-facing public version for a server row version: a fresh Rulebook row is v1 (private, unpublished),
 * each public submission stages `version + 1` and approval bumps the row — so displayed = row version - 1.
 */
export const toPublicVersion = (rowVersion) => rowVersion - 1;
/** An update of the linked Rulebook under its lock token, or a first publish carrying the draft's publish key. */
const toPublishRequest = ({ rulebookId, expectedUpdatedAt, publishKey, ...request }) => {
    if (rulebookId === undefined) {
        return { ...request, ...(publishKey !== undefined && { publishKey }) };
    }
    return { ...request, rulebookId, ...(expectedUpdatedAt !== undefined && { expectedUpdatedAt }) };
};
/** The draft's server link for `ownerId` — read from that owner's storage even after an account switch. */
const linkedRulebookId = (draftId, ownerId) => getRulebookDraft({ id: draftId, storage: draftStorageForOwner(ownerId), ownerId })?.rulebook_id;
/** Reads the draft's server copy; one that is gone unlinks the draft, so its next publish creates a new Rulebook. */
const loadPublishedRecord = async (client, draftId, 
/** The Rulebook to read when the link is missing (a replayed first publish whose relink couldn't be stored). */
knownRulebookId) => {
    // The account the read is for: a switch while it is in flight must not touch the next account's links.
    const ownerId = getRulebookDraftOwner();
    const rulebookId = linkedRulebookId(draftId, ownerId) ?? knownRulebookId;
    if (!rulebookId) {
        return EMPTY_PUBLISHED_RECORD;
    }
    const record = await client.getRulebook({ rulebookId });
    // Deleted on the server (or moderated away). Only forget the link if it still names what was fetched: another tab
    // may have relinked the draft while the read was in flight.
    if (!record && linkedRulebookId(draftId, ownerId) === rulebookId) {
        linkRulebookDraft({ draftId, rulebookId: null, ownerId });
    }
    return record ?? EMPTY_PUBLISHED_RECORD;
};
/**
 * After a save succeeded, the untouched draft is removed — Edit re-imports from the server. A draft that changed
 * while the request was in flight (or one that can't be removed, or whose owner signed out) is kept and linked to
 * the Rulebook it just wrote so a later save updates instead of duplicating.
 */
const cleanupSavedDraft = ({ draftId, draftStamp, savedId, publishingOwner, }) => {
    const isUntouched = getRulebookDraftOwner() === publishingOwner && getRulebookDraft({ id: draftId })?.updated_at === draftStamp;
    if (isUntouched && deleteRulebookDraft({ id: draftId })) {
        return;
    }
    if (isUntouched) {
        logger.warn("Could not remove the saved rulebook draft");
    }
    linkRulebookDraft({ draftId, rulebookId: savedId, ownerId: publishingOwner });
};
/** Classify a failed publish: conflict/missing errors reload the live copy for the next save; anything else is a plain error. */
const recoverPublishFailure = async ({ client, draftId, publishingOwner, error, }) => {
    const isConflict = error instanceof RulebookPublishConflictError;
    // A deleted Rulebook reloads the same way; the reload returns null and the save reports itself missing.
    const isReloadable = isConflict || error instanceof RulebookPublishMissingError;
    if (!isReloadable) {
        return { fresh: null, isReloadable };
    }
    if (isConflict && error.rulebookId !== undefined) {
        // A repeated first publish found the Rulebook this draft already made: restore the lost link, then reload it.
        linkRulebookDraft({ draftId, rulebookId: error.rulebookId, ownerId: publishingOwner });
    }
    const knownRulebookId = isConflict ? error.rulebookId : undefined;
    const fresh = await loadPublishedRecord(client, draftId, knownRulebookId).catch(() => null);
    return { fresh, isReloadable };
};
/**
 * Draft-vs-published state for the Editor header: live version, version in review, unpublished change count, and
 * the Save / Submit for review / Discard actions. The draft record carries the server Rulebook it edits
 * (`rulebook_id`, set by the import that created it); a successful save removes the draft — Edit re-imports.
 */
export default function useRulebookPublishing({ definition, meta, client = apiRulebookPublishingClient }) {
    const [stateRecord, setStateRecord] = useState(null);
    const [stateStatus, setStateStatus] = useState("loading");
    // Every load and publish takes a ticket; only the latest one may write state, so a slow older response
    // (or one that lands after unmount or a Rulebook switch) can't overwrite newer data.
    const refLatestRequest = useRef(0);
    useEffect(() => {
        refLatestRequest.current += 1;
        const ticket = refLatestRequest.current;
        if (!getRulebookDraft({ id: definition.id })?.rulebook_id) {
            setStateRecord(EMPTY_PUBLISHED_RECORD);
            setStateStatus("idle");
            return undefined;
        }
        setStateStatus("loading");
        loadPublishedRecord(client, definition.id)
            .then((record) => {
            if (refLatestRequest.current === ticket) {
                setStateRecord(record);
                setStateStatus("idle");
            }
        })
            .catch(() => {
            if (refLatestRequest.current === ticket) {
                setStateStatus("error");
            }
        });
        return () => {
            refLatestRequest.current += 1;
        };
    }, [client, definition.id]);
    const baseline = useMemo(() => (stateRecord ? toPublishedRulebookSnapshot({ record: stateRecord, draftId: definition.id }) : null), [stateRecord, definition.id]);
    const unpublishedChanges = useMemo(() => (baseline ? countRulebookChanges({ baseline, current: { definition, meta } }) : null), [baseline, definition, meta]);
    const loadedLiveId = stateRecord?.live?.id;
    const expectedUpdatedAt = stateRecord?.live?.updatedAt;
    /**
     * Saves or submits the draft; `null` when that fails. Throws `RulebookPublishConflictError` when the server copy
     * changed since it was read (or was never read here), after reloading it so the next save locks against it, and
     * `RulebookPublishRejectedError` when the server refused the write (its code/message go to the caller's UI).
     * A successful publish removes the draft — Edit always re-imports from the server copy.
     */
    const publish = useCallback(async () => {
        refLatestRequest.current += 1;
        const ticket = refLatestRequest.current;
        setStateStatus("publishing");
        // The account publishing; the draft is removed for it even if the user switches accounts before the response lands.
        const publishingOwner = getRulebookDraftOwner();
        const draft = getRulebookDraft({ id: definition.id });
        // A publish without a draft would create a second Rulebook; refuse rather than duplicate.
        if (draft === undefined) {
            logger.warn("Rulebook draft missing at publish; refusing to create a duplicate");
            setStateStatus("error");
            return null;
        }
        const publishKey = draft.publish_key;
        const draftStamp = draft.updated_at;
        // The loaded copy still identifies the Rulebook if the draft was saved before `rulebook_id` existed.
        const rulebookId = draft.rulebook_id ?? loadedLiveId;
        try {
            // A linked Rulebook this editor never loaded has no lock token: reload it instead of overwriting it blind.
            if (rulebookId !== undefined && loadedLiveId !== rulebookId) {
                throw new RulebookPublishConflictError();
            }
            const record = await client.publishRulebook(toPublishRequest({ definition, meta, rulebookId, expectedUpdatedAt, publishKey }));
            // The saved draft's job is done: Edit re-imports from the server, so keeping it would strand a stale copy.
            const savedId = record.live?.id ?? rulebookId ?? null;
            cleanupSavedDraft({ draftId: definition.id, draftStamp, savedId, publishingOwner });
            if (refLatestRequest.current === ticket) {
                setStateRecord(record);
                setStateStatus("idle");
            }
            return record;
        }
        catch (error) {
            const { fresh, isReloadable } = await recoverPublishFailure({ client, draftId: definition.id, publishingOwner, error });
            if (refLatestRequest.current === ticket) {
                setStateRecord((previous) => fresh ?? previous);
                setStateStatus(fresh ? "idle" : "error");
            }
            if (isReloadable || error instanceof RulebookPublishRejectedError) {
                throw error;
            }
            return null;
        }
    }, [client, definition, meta, loadedLiveId, expectedUpdatedAt]);
    const liveVersion = stateRecord?.live?.version ?? 0;
    // Creator-facing versions count only the public flow: the private row stays v1 forever, a first submission
    // stages row v2 (shown as v1 in review), and approval bumps the row again — so displayed = row version - 1.
    const publishedVersion = stateRecord?.live && stateRecord.live.published_at !== undefined ? toPublicVersion(stateRecord.live.version) : 0;
    const pendingVersion = stateRecord?.pending ? toPublicVersion(stateRecord.pending.version) : null;
    const isPublic = meta.visibility === RulebookVisibility.PUBLIC;
    return {
        status: stateStatus,
        record: stateRecord,
        /** What Discard draft restores: the version in review, else the live one; `null` when never published. */
        baseline,
        /** `null` while loading or before the first publish. */
        unpublishedChanges,
        liveVersion,
        /** The approved public version (0 = never published or private-only saves; versions are a public-flow concept). */
        publishedVersion,
        /** The public version under review (`null` when nothing is in review). */
        pendingVersion,
        /**
         * The public version a Submit stages: the one already in review, else the next after the published one.
         * Private saves do not version.
         */
        nextVersion: pendingVersion ?? publishedVersion + 1,
        isPublic,
        publish,
    };
}
